import React, {
  useContext, useState, useEffect, useRef,
} from 'react';
import { useWindowDimensions, Platform } from 'react-native';
import { WebView } from 'react-native-webview';
import {
  Button, Dialog, Portal,
} from 'react-native-paper';
import { NativeBaseProvider, ScrollView } from 'native-base';
import { getFileType } from '../../utils/utils';
import { ModalContext } from '../../context/ModalContext';
import LoadingIndicator from '../../components/commons/LoadingIndicator';
import ZoomableImage from '../../components/commons/ZoomableImage';

const pdfViewerHtml = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, minimum-scale=1.0" />
  <title>PDF Viewer</title>
  <script src="https://cdn.jsdelivr.net/npm/pdfjs-dist@2.11.338/build/pdf.min.js"></script>
  <style>
    body, html { margin: 0; padding: 0; background-color: #333; }
    #viewer {
      width: 100%;
    }
    canvas {
      display: block;
      width: 100%;
      height: auto;
      margin-bottom: 4px;
    }
    #loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100vh;
      color: white;
      font-family: sans-serif;
    }
  </style>
</head>
<body>
  <div id="loading-container">Loading PDF...</div>
  <div id="viewer"></div>

  <script>
    pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.11.338/build/pdf.worker.min.js';

    function renderPdf(base64) {
      try {
        const pdfData = atob(base64);
        const loadingTask = pdfjsLib.getDocument({ data: pdfData });

        loadingTask.promise.then(function(pdf) {
          document.getElementById('loading-container').style.display = 'none';
          
          const pagePromises = [];
          for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            pagePromises.push(pdf.getPage(pageNum));
          }
          return Promise.all(pagePromises);

        }).then(function(pages) {
          const viewer = document.getElementById('viewer');
          pages.forEach(function(page, index) {
            const canvas = document.createElement('canvas');
            canvas.id = 'page-' + (index + 1);
            viewer.appendChild(canvas);

            const context = canvas.getContext('2d');
            
            const desiredWidth = document.body.clientWidth;
            const pixelDensity = Math.min(window.devicePixelRatio || 1, 2);
            const viewport = page.getViewport({ scale: 1 });
            const scale = (desiredWidth / viewport.width) * pixelDensity;
            const scaledViewport = page.getViewport({ scale: scale });

            canvas.height = scaledViewport.height;
            canvas.width = scaledViewport.width;

            const renderContext = {
              canvasContext: context,
              viewport: scaledViewport
            };
            page.render(renderContext);
          });
        }).catch(function(err) {
            document.getElementById('loading-container').textContent = 'Error rendering PDF: ' + err.message;
            window.ReactNativeWebView.postMessage('Error: ' + err.message);
        });
      } catch (err) {
        document.getElementById('loading-container').textContent = 'Error decoding base64: ' + err.message;
        window.ReactNativeWebView.postMessage('Error: ' + err.message);
      }
    }

    const messageListener = (event) => {
      renderPdf(event.data);
    };

    window.addEventListener('message', messageListener);
    document.addEventListener('message', messageListener);

  </script>
</body>
</html>
`;

function DocumentViewer() {
  const {
    displayDocumentViewerModal,
    toggleDocumentViewerModal,
  } = useContext(ModalContext);

  const [loading, setLoading] = useState(true);
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const webViewRef = useRef(null);

  const fileType = displayDocumentViewerModal.file
    ? getFileType(displayDocumentViewerModal.file)
    : undefined;

  useEffect(() => {
    if (displayDocumentViewerModal.file) {
      setLoading(false);
    }
  }, [displayDocumentViewerModal.file]);

  if (!displayDocumentViewerModal.display) return null;

  const renderPdf = () => {
    const handleOnLoad = () => {
      if (webViewRef.current && displayDocumentViewerModal.file) {
        const base64Data = displayDocumentViewerModal.file.split(',')[1];
        if (base64Data) {
          if (Platform.OS === 'web') {
            webViewRef.current.contentWindow.postMessage(base64Data, '*');
          } else {
            webViewRef.current.postMessage(base64Data);
          }
        }
      }
    };

    const handleMessage = (event) => {
      console.error('Error from WebView:', event.nativeEvent.data);
    };

    if (Platform.OS === 'web') {
      return (
        <iframe
          ref={webViewRef}
          srcDoc={pdfViewerHtml}
          style={{ width: '80vw', height: '60vh', border: 'none' }}
          title={displayDocumentViewerModal.filename}
          onLoad={handleOnLoad}
        />
      );
    }

    return (
      <WebView
        ref={webViewRef}
        source={{ html: pdfViewerHtml }}
        style={{
          width: windowWidth * 0.8,
          height: windowHeight * 0.6,
          backgroundColor: '#333',
        }}
        originWhitelist={['*']}
        onLoadEnd={handleOnLoad}
        onMessage={handleMessage}
        javaScriptEnabled
        domStorageEnabled
        onError={(e) => console.error('WebView Error:', e.nativeEvent)}
      />
    );
  };

  return (
    <Dialog
      visible={displayDocumentViewerModal.display}
      dismissable
      onDismiss={toggleDocumentViewerModal}
    >
      <Dialog.Title>
        {displayDocumentViewerModal.filename}
      </Dialog.Title>
      <Dialog.ScrollArea>
        <ScrollView
          height="60vh"
          horizontal
          scrollEnabled={!(Platform.OS !== 'web' && fileType === 'application/pdf')}
          contentContainerStyle={{ alignItems: 'center', height: Platform.OS !== 'web' ? 450 : '' }}
        >
          { fileType === 'application/pdf'
            ? renderPdf()
            : (
              loading ? (
                <LoadingIndicator text="Chargement du document..." />
              ) : (
                <ZoomableImage
                  source={{ uri: displayDocumentViewerModal.file }}
                  style={{
                    alignSelf: 'center',
                  }}
                />
              )
            )}
        </ScrollView>
      </Dialog.ScrollArea>
      <Dialog.Actions>
        <Button onPress={toggleDocumentViewerModal}>
          Fermer
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}

export default function DocumentViewerModal() {
  return (
    <Portal>
      <NativeBaseProvider>
        <DocumentViewer />
      </NativeBaseProvider>
    </Portal>
  );
}
